import * as THREE from 'three';
import type { System } from '../../systems';
import type { InputManager } from '../../input';
import type { AudioManager } from '../../audio';
import type { Inventory } from '../inventory';
import type { ShipUpgrades } from '../upgrades';
import type { Explosions } from '../combat';
import type { OreDeposit } from '../surface';
import { FpsMiningBeam } from './FpsMiningBeam';
import { rollYield } from '../sector';

export interface FpsMiningToolHooks {
  /** Fired after a deposit is destroyed and yield is added to inventory.
   *  Useful for the surface scene to clean up its obstacle/deposit
   *  lists, and for mission code to track how much surface ore the
   *  player has collected with the cutter. */
  readonly onDepositDestroyed?: (
    deposit: OreDeposit,
    yieldAmount: number
  ) => void;
  /** Fired whenever the equipped state flips. Drives the HUD indicator. */
  readonly onEquipChange?: (equipped: boolean) => void;
}

export interface FpsMiningToolOptions {
  /** Damage per second dealt to the targeted deposit. Tuned so a typical
   *  rare-rarity deposit (max health ~6) breaks in roughly a second of
   *  sustained beaming. */
  readonly damagePerSecond: number;
  /** Maximum reach in metres. Past this the beam draws but does no damage. */
  readonly range: number;
}

export const DEFAULT_FPS_MINING_TOOL: FpsMiningToolOptions = {
  damagePerSecond: 9,
  range: 22
};

const MOUSE_LEFT = 0;
const TOGGLE_KEY = 'Digit2';
const VIEWMODEL_OFFSET = new THREE.Vector3(0.30, -0.30, -0.50);
// Emitter sits at z = -0.81 inside the viewmodel, plus the viewmodel's
// own -0.50 offset → camera-local muzzle world ≈ z = -1.31.
const MUZZLE_OFFSET = new THREE.Vector3(0.30, -0.30, -1.31);
const STOW_TARGET_Y = -0.62;

/**
 * Hand-held mining cutter. Equipable on planet surfaces with the `2` key
 * once the player has bought it at the station's weapon counter. While
 * equipped, left-click fires a continuous green beam that breaks ore
 * deposits and routes their yield into the suit (FPS) inventory.
 *
 * The viewmodel is parented to the scene root and re-pinned to the camera
 * each frame, since our camera lives outside the scene graph.
 */
export class FpsMiningTool implements System {
  readonly id = 'fps-mining-tool';

  private readonly viewmodel: THREE.Group;
  private readonly emitterCore: THREE.Mesh;
  private readonly emitterRing: THREE.Mesh;
  private readonly emitterCoreMat: THREE.MeshBasicMaterial;
  private readonly emitterRingMat: THREE.MeshBasicMaterial;
  private readonly accentMat: THREE.MeshStandardMaterial;
  private readonly beam: FpsMiningBeam;

  private readonly muzzleWorld = new THREE.Vector3();
  private readonly cameraForward = new THREE.Vector3();
  private readonly aimPoint = new THREE.Vector3();
  private readonly rayDir = new THREE.Vector3();
  private readonly toDeposit = new THREE.Vector3();
  private readonly hitPoint = new THREE.Vector3();
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly tmpOffset = new THREE.Vector3();

  private enabled = true;
  private equipped = false;
  private firing = false;
  private equipPose = 0; // 0 stowed → 1 raised
  private firePulse = 0;
  private tickAccumulator = 0;
  private disposed = false;

  constructor(
    private readonly camera: THREE.Camera,
    private readonly input: InputManager,
    private readonly shipUpgrades: ShipUpgrades,
    private readonly fpsInventory: Inventory,
    private readonly explosions: Explosions,
    private readonly audio: AudioManager | null,
    private readonly worldRoot: THREE.Object3D,
    private readonly getDeposits: () => readonly OreDeposit[],
    private readonly options: FpsMiningToolOptions = DEFAULT_FPS_MINING_TOOL,
    private readonly hooks: FpsMiningToolHooks = {}
  ) {
    const built = buildCutterViewmodel();
    this.viewmodel = built.group;
    this.emitterCore = built.emitterCore;
    this.emitterRing = built.emitterRing;
    this.emitterCoreMat = built.emitterCoreMat;
    this.emitterRingMat = built.emitterRingMat;
    this.accentMat = built.accentMat;
    this.viewmodel.visible = false;
    this.worldRoot.add(this.viewmodel);

    this.beam = new FpsMiningBeam();
    this.worldRoot.add(this.beam.object3D);
  }

  setEnabled(value: boolean): void {
    this.enabled = value;
    if (!value) {
      this.firing = false;
      this.beam.hide();
      // Once disabled, our update loop stops running — the viewmodel
      // would otherwise stay frozen at the camera's last pose (visible
      // floating at chest height when the player switches to the
      // ship's chase camera). Snap it to the stowed/hidden state so it
      // disappears immediately and re-emerges cleanly on re-enable.
      this.equipped = false;
      this.equipPose = 0;
      this.firePulse = 0;
      this.viewmodel.visible = false;
      this.applyEmitterIntensity();
      this.hooks.onEquipChange?.(false);
    }
  }

  isUnlocked(): boolean {
    return this.shipUpgrades.has('fps-mining-tool');
  }

  isEquipped(): boolean {
    return this.equipped;
  }

  isFiring(): boolean {
    return this.firing;
  }

  /** Force the tool into a stowed/unequipped state without firing the
   *  hook. Used when leaving FPS mode so the next entry starts clean. */
  forceStow(): void {
    if (!this.equipped) return;
    this.equipped = false;
    this.firing = false;
    this.beam.hide();
    this.hooks.onEquipChange?.(false);
  }

  update(dt: number): void {
    if (this.disposed) return;

    // Toggle handling — only meaningful when unlocked and FPS controls active.
    if (
      this.enabled &&
      this.input.isPointerLocked() &&
      this.input.isKeyJustPressed(TOGGLE_KEY)
    ) {
      if (this.isUnlocked()) {
        this.equipped = !this.equipped;
        if (!this.equipped) {
          this.firing = false;
          this.beam.hide();
        }
        this.audio?.playPrompt();
        this.hooks.onEquipChange?.(this.equipped);
      }
    }

    // Smoothly raise / stow the viewmodel.
    const targetPose = this.equipped ? 1 : 0;
    const lerpRate = 8;
    this.equipPose += (targetPose - this.equipPose) * Math.min(1, dt * lerpRate);

    this.updateViewmodelTransform();

    // Fire only when actually equipped and pointer is locked into the world.
    const wantsFire =
      this.enabled &&
      this.equipped &&
      this.input.isPointerLocked() &&
      this.input.isMouseDown(MOUSE_LEFT);

    if (!wantsFire) {
      if (this.firing) {
        this.firing = false;
        this.beam.hide();
        this.tickAccumulator = 0;
      }
      // Decay fire pulse so the emitter calms down.
      this.firePulse = Math.max(0, this.firePulse - dt * 4);
      this.applyEmitterIntensity();
      return;
    }

    this.fire(dt);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.beam.hide();
    this.beam.dispose();
    if (this.beam.object3D.parent) {
      this.beam.object3D.parent.remove(this.beam.object3D);
    }
    this.viewmodel.visible = false;
    if (this.viewmodel.parent) {
      this.viewmodel.parent.remove(this.viewmodel);
    }
    disposeViewmodel(this.viewmodel);
    this.equipped = false;
    this.firing = false;
  }

  private updateViewmodelTransform(): void {
    if (this.equipPose <= 0.001 && !this.equipped) {
      this.viewmodel.visible = false;
      return;
    }
    this.viewmodel.visible = true;

    this.camera.updateMatrixWorld();
    this.tmpOffset.copy(VIEWMODEL_OFFSET);
    // Slide the viewmodel down while stowed for a quick raise/lower beat.
    this.tmpOffset.y += STOW_TARGET_Y * (1 - this.equipPose);
    this.tmpOffset.applyQuaternion(this.camera.quaternion);

    this.viewmodel.position
      .copy(this.camera.position)
      .add(this.tmpOffset);
    this.viewmodel.quaternion.copy(this.camera.quaternion);

    // Subtle recoil: nudge the viewmodel back along its forward axis when
    // firing, eased via the fire pulse so taps don't visibly twitch.
    if (this.firePulse > 0) {
      this.tmpQuat.copy(this.camera.quaternion);
      this.tmpOffset.set(0, 0, this.firePulse * 0.04).applyQuaternion(this.tmpQuat);
      this.viewmodel.position.add(this.tmpOffset);
    }
  }

  private fire(dt: number): void {
    this.firing = true;

    // Build the muzzle origin in world space using the camera transform —
    // same idea as the ship beam, just with the FPS offset.
    this.tmpOffset.copy(MUZZLE_OFFSET).applyQuaternion(this.camera.quaternion);
    this.muzzleWorld.copy(this.camera.position).add(this.tmpOffset);

    this.cameraForward.set(0, 0, -1).applyQuaternion(this.camera.quaternion);
    this.aimPoint
      .copy(this.camera.position)
      .addScaledVector(this.cameraForward, this.options.range);

    this.rayDir.copy(this.aimPoint).sub(this.muzzleWorld);
    const rayLength = this.rayDir.length();
    if (rayLength < 1e-3) {
      this.beam.hide();
      this.firing = false;
      return;
    }
    this.rayDir.multiplyScalar(1 / rayLength);

    const target = this.findClosestDeposit(this.muzzleWorld, this.rayDir);
    if (target) {
      this.beam.setEndpoints(this.muzzleWorld, this.hitPoint);
      this.beam.tick(dt);

      const damage = this.options.damagePerSecond * dt;
      const destroyed = target.takeDamage(damage);
      this.firePulse = Math.min(1, this.firePulse + dt * 6);
      this.applyEmitterIntensity();
      this.tickAudio(dt);

      if (destroyed) {
        this.handleDestroy(target);
      } else if (Math.random() < 0.45) {
        this.explosions.spawnHit(this.hitPoint);
      }
    } else {
      // No target — beam still draws to max range so the player sees it.
      this.aimPoint
        .copy(this.muzzleWorld)
        .addScaledVector(this.rayDir, this.options.range);
      this.beam.setEndpoints(this.muzzleWorld, this.aimPoint);
      this.beam.tick(dt);
      this.firePulse = Math.min(1, this.firePulse + dt * 3);
      this.applyEmitterIntensity();
    }
  }

  private handleDestroy(target: OreDeposit): void {
    const yieldAmount = rollYield(target.resource);
    if (yieldAmount > 0) {
      this.fpsInventory.addItem(target.resource.itemId, yieldAmount);
    }
    const pos = target.object3D.position;
    this.explosions.spawnDestruction(
      pos,
      target.collisionRadius,
      target.definition.accent
        ? parseHex(target.definition.accent)
        : 0xb7ff6a
    );
    this.audio?.playExplosion(target.collisionRadius);
    this.hooks.onDepositDestroyed?.(target, yieldAmount);
    target.markForRemoval();
  }

  /** Iterate live deposits and find the one whose bounding sphere first
   *  intersects the aiming ray. Same approach the ShipMiningBeam uses for
   *  asteroids. */
  private findClosestDeposit(
    origin: THREE.Vector3,
    dir: THREE.Vector3
  ): OreDeposit | null {
    let best: OreDeposit | null = null;
    let bestT = this.options.range;

    for (const deposit of this.getDeposits()) {
      if (!deposit.isAlive) continue;
      this.toDeposit.copy(deposit.object3D.position).sub(origin);
      const projection = this.toDeposit.dot(dir);
      if (
        projection < -deposit.collisionRadius ||
        projection > this.options.range + deposit.collisionRadius
      ) {
        continue;
      }
      const perpSq =
        this.toDeposit.lengthSq() - projection * projection;
      const radiusSq = deposit.collisionRadius * deposit.collisionRadius;
      if (perpSq > radiusSq) continue;

      const sphereT = projection - Math.sqrt(Math.max(radiusSq - perpSq, 0));
      const enterT = Math.max(sphereT, 0);
      if (enterT < bestT) {
        bestT = enterT;
        best = deposit;
      }
    }

    if (best) {
      this.hitPoint.copy(origin).addScaledVector(dir, bestT);
    }
    return best;
  }

  private applyEmitterIntensity(): void {
    const base = this.equipped ? 0.55 : 0.0;
    const intensity = base + this.firePulse * 1.4;
    this.emitterCoreMat.opacity = Math.min(1, 0.45 + intensity * 0.55);
    this.emitterRingMat.opacity = Math.min(1, 0.32 + intensity * 0.6);
    this.accentMat.emissiveIntensity = 0.3 + this.firePulse * 0.9;
  }

  private tickAudio(dt: number): void {
    if (!this.audio) return;
    this.tickAccumulator += dt;
    if (this.tickAccumulator >= 0.32) {
      this.audio.playMiningTick();
      this.tickAccumulator = 0;
    }
  }
}

interface ViewmodelBuild {
  readonly group: THREE.Group;
  readonly emitterCore: THREE.Mesh;
  readonly emitterRing: THREE.Mesh;
  readonly emitterCoreMat: THREE.MeshBasicMaterial;
  readonly emitterRingMat: THREE.MeshBasicMaterial;
  readonly accentMat: THREE.MeshStandardMaterial;
}

/** Build the cutter viewmodel. The silhouette is a chunky industrial
 *  cutter rifle: angled stock at the rear, raised power-core box on top,
 *  ergonomic grip, vented forestock, and a heavy two-stage emitter
 *  assembly at the muzzle. The accent colour ties it visually to the
 *  ship's mining beam.
 *
 *  All Z values are negative-forward (camera convention) — nothing here
 *  tracks the world, the parent system pins the whole group to the
 *  camera each frame. */
function buildCutterViewmodel(): ViewmodelBuild {
  const group = new THREE.Group();
  group.name = 'fps-mining-cutter';

  const hull = new THREE.MeshStandardMaterial({
    color: 0x4a5566,
    metalness: 0.55,
    roughness: 0.45,
    flatShading: true
  });
  const hullDark = new THREE.MeshStandardMaterial({
    color: 0x2a323e,
    metalness: 0.6,
    roughness: 0.55,
    flatShading: true
  });
  const dark = new THREE.MeshStandardMaterial({
    color: 0x12161e,
    metalness: 0.35,
    roughness: 0.7,
    flatShading: true
  });
  const accent = new THREE.MeshStandardMaterial({
    color: 0x9aff3c,
    emissive: 0x9aff3c,
    emissiveIntensity: 0.4,
    roughness: 0.35,
    metalness: 0.25,
    flatShading: true
  });
  accent.toneMapped = false;

  // ── Receiver / main body ───────────────────────────────────────────
  // A flatter wider slab — the gun's "frame". Sits at the centre of the
  // group so positions read as offsets from the action.
  const receiver = new THREE.Mesh(
    new THREE.BoxGeometry(0.16, 0.11, 0.34),
    hull
  );
  group.add(receiver);

  // Lower receiver — slightly narrower, slightly inset under the main
  // frame, dark to break the silhouette.
  const lower = new THREE.Mesh(
    new THREE.BoxGeometry(0.14, 0.04, 0.30),
    hullDark
  );
  lower.position.set(0, -0.072, 0);
  group.add(lower);

  // ── Power-core housing on top ──────────────────────────────────────
  // A raised box behind the action with vented sides and a glowing slot
  // through the middle — the "battery pack" the cutter draws from.
  const coreBox = new THREE.Mesh(
    new THREE.BoxGeometry(0.13, 0.08, 0.18),
    hull
  );
  coreBox.position.set(0, 0.085, 0.05);
  group.add(coreBox);

  // Glowing core slot — dark recessed strip with an emissive accent insert.
  const coreSlot = new THREE.Mesh(
    new THREE.BoxGeometry(0.085, 0.022, 0.18),
    dark
  );
  coreSlot.position.set(0, 0.118, 0.05);
  group.add(coreSlot);

  const coreInsert = new THREE.Mesh(
    new THREE.BoxGeometry(0.06, 0.014, 0.16),
    accent
  );
  coreInsert.position.set(0, 0.121, 0.05);
  group.add(coreInsert);

  // Two ribs straddling the core — adds detail to the top.
  for (let side = -1; side <= 1; side += 2) {
    const rib = new THREE.Mesh(
      new THREE.BoxGeometry(0.012, 0.05, 0.18),
      hullDark
    );
    rib.position.set(side * 0.058, 0.105, 0.05);
    group.add(rib);
  }

  // Top sight rail running forward of the core — gives the silhouette a
  // recognisable "weapon" read from any angle.
  const rail = new THREE.Mesh(
    new THREE.BoxGeometry(0.04, 0.018, 0.18),
    hullDark
  );
  rail.position.set(0, 0.065, -0.13);
  group.add(rail);

  for (let i = 0; i < 4; i++) {
    const tooth = new THREE.Mesh(
      new THREE.BoxGeometry(0.052, 0.005, 0.012),
      hull
    );
    tooth.position.set(0, 0.076, -0.07 - i * 0.04);
    group.add(tooth);
  }

  // ── Stock at the rear ──────────────────────────────────────────────
  // Angled wedge that suggests the gun braces against the shoulder.
  const stock = new THREE.Mesh(
    new THREE.BoxGeometry(0.13, 0.085, 0.06),
    hullDark
  );
  stock.position.set(0, -0.005, 0.20);
  group.add(stock);

  const stockCap = new THREE.Mesh(
    new THREE.BoxGeometry(0.11, 0.07, 0.02),
    dark
  );
  stockCap.position.set(0, -0.008, 0.232);
  group.add(stockCap);

  // Tiny power-status dot on the stock — visible when looking down at
  // the weapon.
  const statusDot = new THREE.Mesh(
    new THREE.SphereGeometry(0.008, 10, 8),
    new THREE.MeshBasicMaterial({
      color: 0x9aff3c,
      transparent: true,
      opacity: 0.95,
      toneMapped: false
    })
  );
  statusDot.position.set(0.04, 0.022, 0.241);
  group.add(statusDot);

  // ── Grip + trigger guard ───────────────────────────────────────────
  // Slight rake forward feels more natural than a vertical handle.
  const grip = new THREE.Mesh(
    new THREE.BoxGeometry(0.06, 0.14, 0.055),
    dark
  );
  grip.position.set(0, -0.135, 0.05);
  grip.rotation.x = -0.22;
  group.add(grip);

  // Three faux finger ridges along the front of the grip.
  for (let i = 0; i < 3; i++) {
    const ridge = new THREE.Mesh(
      new THREE.BoxGeometry(0.062, 0.006, 0.012),
      hullDark
    );
    ridge.position.copy(grip.position);
    ridge.position.y -= 0.025 + i * 0.025;
    ridge.position.z -= 0.022 + i * 0.005;
    ridge.rotation.copy(grip.rotation);
    group.add(ridge);
  }

  // Trigger guard with a small accent trigger inside.
  const guard = new THREE.Mesh(
    new THREE.TorusGeometry(0.046, 0.008, 8, 16, Math.PI),
    hullDark
  );
  guard.position.set(0, -0.07, 0.04);
  guard.rotation.set(Math.PI / 2, 0, 0);
  group.add(guard);

  const trigger = new THREE.Mesh(
    new THREE.BoxGeometry(0.012, 0.025, 0.006),
    accent
  );
  trigger.position.set(0, -0.062, 0.04);
  trigger.rotation.x = -0.2;
  group.add(trigger);

  // ── Forestock / handguard ──────────────────────────────────────────
  // Thick block running ahead of the receiver, vented on the sides.
  const fore = new THREE.Mesh(
    new THREE.BoxGeometry(0.13, 0.082, 0.18),
    hull
  );
  fore.position.set(0, -0.005, -0.20);
  group.add(fore);

  // Vent cuts on each side — emissive accent backing makes them read as
  // glowing intakes rather than flat decals.
  for (let side = -1; side <= 1; side += 2) {
    const ventBacking = new THREE.Mesh(
      new THREE.BoxGeometry(0.006, 0.038, 0.12),
      accent
    );
    ventBacking.position.set(side * 0.058, 0, -0.20);
    group.add(ventBacking);

    for (let i = -1; i <= 1; i++) {
      const slat = new THREE.Mesh(
        new THREE.BoxGeometry(0.014, 0.04, 0.012),
        hullDark
      );
      slat.position.set(side * 0.063, 0, -0.16 + i * 0.04);
      group.add(slat);
    }
  }

  // ── Barrel + emitter assembly ──────────────────────────────────────
  // Two-stage cylinder: a fat cooling sleeve and a slimmer focusing barrel
  // ahead of it, with three accent rings — the same gold-on-iron rhythm
  // the asteroid miner ships use, in green.
  const cooler = new THREE.Mesh(
    new THREE.CylinderGeometry(0.052, 0.058, 0.12, 14),
    hull
  );
  cooler.rotation.x = Math.PI / 2;
  cooler.position.set(0, 0, -0.34);
  group.add(cooler);

  for (let i = 0; i < 3; i++) {
    const fin = new THREE.Mesh(
      new THREE.TorusGeometry(0.058, 0.005, 6, 20),
      hullDark
    );
    fin.rotation.y = Math.PI / 2;
    fin.position.set(0, 0, -0.30 - i * 0.025);
    group.add(fin);
  }

  const barrel = new THREE.Mesh(
    new THREE.CylinderGeometry(0.034, 0.040, 0.40, 16),
    hull
  );
  barrel.rotation.x = Math.PI / 2;
  barrel.position.set(0, 0, -0.60);
  group.add(barrel);

  // Glowing rings along the focusing barrel.
  for (let i = 0; i < 3; i++) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.038, 0.0055, 6, 18),
      accent
    );
    ring.rotation.y = Math.PI / 2;
    ring.position.set(0, 0, -0.46 - i * 0.10);
    group.add(ring);
  }

  // Muzzle prongs — three short claws around the front lens that frame
  // the emitter and read clearly as "where the laser comes out".
  const prongMat = hullDark;
  for (let i = 0; i < 3; i++) {
    const angle = (i / 3) * Math.PI * 2 + Math.PI / 2;
    const prong = new THREE.Mesh(
      new THREE.BoxGeometry(0.014, 0.05, 0.06),
      prongMat
    );
    prong.position.set(
      Math.cos(angle) * 0.044,
      Math.sin(angle) * 0.044,
      -0.81
    );
    prong.lookAt(new THREE.Vector3(0, 0, -1));
    group.add(prong);
  }

  // ── Emitter glow ───────────────────────────────────────────────────
  const emitterRingMat = new THREE.MeshBasicMaterial({
    color: 0x9aff3c,
    transparent: true,
    opacity: 0.65,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false
  });
  const emitterRing = new THREE.Mesh(
    new THREE.TorusGeometry(0.05, 0.014, 10, 24),
    emitterRingMat
  );
  emitterRing.rotation.y = Math.PI / 2;
  emitterRing.position.set(0, 0, -0.81);
  emitterRing.renderOrder = 3;
  group.add(emitterRing);

  const emitterCoreMat = new THREE.MeshBasicMaterial({
    color: 0xeaffd0,
    transparent: true,
    opacity: 0.85,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false
  });
  const emitterCore = new THREE.Mesh(
    new THREE.SphereGeometry(0.028, 14, 10),
    emitterCoreMat
  );
  emitterCore.position.set(0, 0, -0.81);
  emitterCore.renderOrder = 3;
  group.add(emitterCore);

  // Soft halo disc behind the emitter — adds glow bleed without adding a
  // big light. Faces the camera (negative Z).
  const halo = new THREE.Mesh(
    new THREE.CircleGeometry(0.10, 22),
    new THREE.MeshBasicMaterial({
      color: 0x9aff3c,
      transparent: true,
      opacity: 0.22,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    })
  );
  halo.position.set(0, 0, -0.812);
  halo.rotation.y = Math.PI;
  group.add(halo);

  return {
    group,
    emitterCore,
    emitterRing,
    emitterCoreMat,
    emitterRingMat,
    accentMat: accent
  };
}

function disposeViewmodel(group: THREE.Object3D): void {
  group.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.geometry?.dispose?.();
    const material = mesh.material as THREE.Material | THREE.Material[] | undefined;
    if (Array.isArray(material)) {
      for (const m of material) m.dispose();
    } else if (material) {
      material.dispose();
    }
  });
}

function parseHex(value: string): number {
  const cleaned = value.startsWith('#') ? value.slice(1) : value;
  return parseInt(cleaned, 16) || 0xffffff;
}
